import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import ReviewPage from "./ReviewPage";
import CustomButton from "../Common/Button";
import LoadingPopup from "../Common/LoadingPopup";
import { GetExamQuestion } from "../../utils/axios/questionAPI";
import { SubmitAnswers } from "../../utils/axios/questionAPI";
import moment from "moment";
import { GetUserInfo } from "../../utils/helper";

// Question Navigation Panel Component
const QuestionNavigator = ({ questions, selectedAnswers, currentQuestionIndex, onQuestionClick }) => {
  const answeredCount = selectedAnswers.filter(a => a && a.OptionId).length;
  const unansweredCount = questions.length - answeredCount;

  return (
    <div style={{
      backgroundColor: "#f8fafc",
      borderRadius: "12px",
      padding: "15px",
      marginBottom: "20px",
      border: "1px solid #e2e8f0",
      boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
    }}>
      <div style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: "12px",
        paddingBottom: "10px",
        borderBottom: "1px solid #e2e8f0"
      }}>
        <h4 style={{ margin: 0, fontSize: "14px", color: "#374151", fontWeight: "600" }}>
          Question Navigator
        </h4>
        <div style={{ display: "flex", gap: "12px", fontSize: "12px" }}>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#22c55e",
              borderRadius: "3px",
              display: "inline-block"
            }}></span>
            Answered: {answeredCount}
          </span>
          <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <span style={{
              width: "12px",
              height: "12px",
              backgroundColor: "#ef4444",
              borderRadius: "3px",
              display: "inline-block"
            }}></span>
            Unanswered: {unansweredCount}
          </span>
        </div>
      </div>
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(10, 1fr)",
        gap: "6px",
        maxHeight: "120px",
        overflowY: "auto",
        padding: "4px"
      }}>
        {questions.map((_, index) => {
          const isAnswered = selectedAnswers[index] && selectedAnswers[index].OptionId;
          const isCurrent = index === currentQuestionIndex;
          
          return (
            <button
              key={index}
              onClick={() => onQuestionClick(index)}
              style={{
                width: "32px",
                height: "32px",
                borderRadius: "6px",
                border: isCurrent ? "2px solid #3b82f6" : "1px solid transparent",
                backgroundColor: isAnswered ? "#22c55e" : "#ef4444",
                color: "white",
                fontSize: "12px",
                fontWeight: isCurrent ? "700" : "500",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s ease",
                transform: isCurrent ? "scale(1.1)" : "scale(1)",
                boxShadow: isCurrent ? "0 2px 8px rgba(59, 130, 246, 0.5)" : "0 1px 2px rgba(0,0,0,0.1)"
              }}
              onMouseEnter={(e) => {
                if (!isCurrent) {
                  e.target.style.transform = "scale(1.05)";
                  e.target.style.boxShadow = "0 2px 6px rgba(0,0,0,0.15)";
                }
              }}
              onMouseLeave={(e) => {
                if (!isCurrent) {
                  e.target.style.transform = "scale(1)";
                  e.target.style.boxShadow = "0 1px 2px rgba(0,0,0,0.1)";
                }
              }}
              title={`Question ${index + 1} - ${isAnswered ? 'Answered' : 'Unanswered'}`}
            >
              {index + 1}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const calculateSecondsLeft = () => {
  const storedStartTime = sessionStorage.getItem("ExamStartTime");
  if (!storedStartTime) return 1800; // 30 minutes in seconds
  const remaining = Math.floor((Number(storedStartTime) + 1800 * 1000 - Date.now()) / 1000);
  return Math.max(0, remaining);
};

function Question() {
  const [userInfo] = useState(GetUserInfo());
  const [questions, setQuestions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const questionsRef = useRef([]);
  useEffect(() => { questionsRef.current = questions; }, [questions]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(calculateSecondsLeft());
  const navigate = useNavigate();
  const warningCount = useRef(0);
  const isSubmitting = useRef(false);
  const lastFullscreenExitTime = useRef(0);
  // Tracks the last time the user interacted (mouse, keyboard, touch).
  // Used to distinguish screen-sleep (idle) from an intentional tab switch.
  const lastInteractionTime = useRef(Date.now());
  // Holds the Screen Wake Lock sentinel so we can release it on unmount.
  const wakeLockRef = useRef(null);

  // Session & Auth Guard
  useEffect(() => {
    const token = sessionStorage.getItem("token");
    const examId = sessionStorage.getItem("ExamId");
    const user = GetUserInfo();

    if (!token || !examId || !user) {
      Swal.fire({
        icon: "warning",
        title: "Session Expired",
        text: "No active exam session was found. Please log in to take the exam.",
        confirmButtonColor: "#F59E0B",
        allowOutsideClick: false,
      }).then(() => {
        navigate("/login");
      });
    }
  }, [navigate]);

  // Countdown Timer - Only runs when questions are loaded
  useEffect(() => {
    if (questions.length === 0) return;

    if (!sessionStorage.getItem("ExamStartTime")) {
      sessionStorage.setItem("ExamStartTime", Date.now().toString());
    }

    setSecondsLeft(calculateSecondsLeft());
    sessionStorage.removeItem("isSubmitting");

    const intervalId = setInterval(() => {
      const newSecondsLeft = calculateSecondsLeft();
      setSecondsLeft(newSecondsLeft);

      if (newSecondsLeft <= 0) {
        clearInterval(intervalId);
        sessionStorage.removeItem("ExamStartTime");
        submitExam();
      }
    }, 1000);

    return () => {
      clearInterval(intervalId);
    };
  }, [questions.length, navigate]);

  // Stamps lastInteractionTime on every user action (used by screen-sleep guard)
  useEffect(() => {
    const update = () => { lastInteractionTime.current = Date.now(); };
    const events = ["mousemove", "keydown", "click", "touchstart", "scroll"];
    events.forEach((e) => window.addEventListener(e, update, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, update));
  }, []);

  // Asks OS not to sleep the screen during the exam.
  // Re-acquires the lock when candidate returns to the tab.
  useEffect(() => {
    if (questions.length === 0) return;
    const requestWakeLock = async () => {
      if ("wakeLock" in navigator) {
        try {
          wakeLockRef.current = await navigator.wakeLock.request("screen");
        } catch (err) {
          console.log("Screen Wake Lock unavailable:", err.message);
        }
      }
    };
    requestWakeLock();
    const onVisible = () => { if (document.visibilityState === "visible") requestWakeLock(); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      wakeLockRef.current?.release().catch(() => {});
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [questions.length]);

  const submitExam = async (isMalpractice = false) => {
    if (questionsRef.current.length === 0) return;
    isSubmitting.current = true;
    let questionId = questionsRef.current.map((item) => item.QuestionId);

    let x = sessionStorage.getItem("SelectedAnswers");
    let parsedX = x ? JSON.parse(x) : Array(questionsRef.current.length).fill(0);
    let optionId = Array.from(parsedX, (item) => item?.OptionId || 0);
    let examId = sessionStorage.getItem("ExamId");
    const userId = userInfo?.UserId || userInfo?.Id || userInfo?.[0]?.UserId || 0;

    let examPayload = {
      ExamId: parseInt(examId),
      QuestionId: questionId,
      OptionId: optionId,
      EndTime: moment().format("YYYY-MM-DD HH:mm:ss"),
      CreatedBy: userId,
      IsMalpractice: isMalpractice ? 1 : 0,
    };

    let submitExamRespone = await SubmitAnswers(examPayload);
    if (submitExamRespone.success) {
      sessionStorage.removeItem("userInfo");
      sessionStorage.removeItem("token");
      sessionStorage.removeItem("ExamId");
      sessionStorage.removeItem("ExamQuestions");
      sessionStorage.removeItem("SelectedAnswers");
      sessionStorage.removeItem("ExamStartTime");
      localStorage.removeItem("startTime");

      if (document.fullscreenElement) {
        document.exitFullscreen().catch(() => { });
      }

      if (isMalpractice) {
        navigate("/malpractice");
      } else {
        await Swal.fire({ icon: 'success', title: 'Session Ended', text: 'Your test has been successfully submitted.' });
        navigate("/thankyou");
      }
    } else {
      // In malpractice situation, even if network failed to submit, force exit to malpractice screen
      if (isMalpractice) {
        sessionStorage.clear();
        localStorage.clear();
        if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => { });
        }
        navigate("/malpractice");
      }
    }
  };

  useEffect(() => {
    const handleLeave = (forceWarning) => {
      if (isSubmitting.current) return;

      setTimeout(() => {
        const isForce = forceWarning === true;

        let isMalpractice = false;
        if (document.hidden || isForce) {
          // Manual screen-off: wake lock released by OS → check idle time
          // Wake lock still held → screen can't sleep → it's a tab switch → malpractice
          if (document.hidden && !isForce) {
            const wakeLockActive = wakeLockRef.current !== null && !wakeLockRef.current.released;
            if (!wakeLockActive) {
              const idleMs = Date.now() - lastInteractionTime.current;
              if (idleMs > 28 * 1000) return; // idle ≥ 28s → OS auto-sleep possible (min 30s ± 2s tolerance) — not malpractice
            }
          }
          isMalpractice = true;
        } else if (!document.hasFocus()) {
          // If we recently exited full screen (within last 500ms), 
          // this blur is likely caused by the exit action itself. Ignore it.
          if (Date.now() - lastFullscreenExitTime.current < 500) {
            return;
          }
          isMalpractice = true;
        }

        if (isMalpractice) {
          if (warningCount.current === 0) {
            warningCount.current = 1;
            Swal.fire({
              icon: 'warning',
              title: 'Warning 1!',
              text: "You are trying to cheat us. Don't be too smart! Stay in our site or else your test will be automatically submitted.",
              confirmButtonText: 'I understand',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          } else if (warningCount.current === 1) {
            warningCount.current = 2;
            Swal.fire({
              icon: 'warning',
              title: 'Warning 2!',
              text: "This is your final warning. Do not leave the site or your test will be automatically submitted due to malpractice.",
              confirmButtonText: 'I understand',
              allowOutsideClick: false,
              allowEscapeKey: false,
            });
          } else if (warningCount.current === 2) {
            warningCount.current = 3;
            Swal.fire({
              icon: 'warning',
              title: 'Test Submitted',
              text: "Test submitted automatically due to malpractice.",
              showConfirmButton: false,
              timer: 3000
            });
            submitExam(true);
          }
        }
      }, 100);
    };

    const trapFullscreen = () => {
      if (!document.fullscreenElement && !isSubmitting.current) {
        document.documentElement.requestFullscreen({ navigationUI: "hide" }).then(() => {
          if (navigator.keyboard && navigator.keyboard.lock) {
            navigator.keyboard.lock(); // Lock all system keys
          }
          const overlay = document.getElementById('fullscreen-enforcer');
          if (overlay) overlay.remove();
        }).catch(() => {
          let overlay = document.getElementById('fullscreen-enforcer');
          if (!overlay) {
            overlay = document.createElement('div');
            overlay.id = 'fullscreen-enforcer';
            Object.assign(overlay.style, {
              position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
              backgroundColor: 'rgba(0,0,0,0.95)', zIndex: 1050, display: 'flex',
              flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
              color: 'white', cursor: 'pointer'
            });
            overlay.innerHTML = `
               <h1 style="color:#ff4444; font-size:2.5rem; margin-bottom:15px; text-align:center;">⚠️ Fullscreen Mode Required</h1>
               <p style="font-size:1.2rem; text-align:center; max-width:600px; line-height:1.5; margin-bottom:30px; color:#e0e0e0;">
                 You have exited fullscreen mode. Active monitoring remains enabled. Accessing other tabs or applications will be flagged as malpractice.
               </p>
               <p style="font-size:1.2rem; font-weight:bold; padding:12px 24px; border:2px solid white; border-radius:8px; background:rgba(255,255,255,0.1);">
                 Click anywhere to resume exam
               </p>
             `;

            overlay.onclick = () => {
              document.documentElement.requestFullscreen({ navigationUI: "hide" }).then(() => {
                if (navigator.keyboard && navigator.keyboard.lock) navigator.keyboard.lock();
                overlay.remove();
              }).catch(() => { });
            };
            document.body.appendChild(overlay);
          }
        });
      }
    };

    const handleFullscreenChange = () => {
      if (isSubmitting.current || sessionStorage.getItem("isSubmitting") === "true") return;
      if (!document.fullscreenElement) {
        lastFullscreenExitTime.current = Date.now();
        trapFullscreen();
      }
    };

    const handleMouseLeave = (e) => {
      // Ignore if mouse leaves through the top edge (e.clientY <= 5) to avoid penalizing accidental hovers near browser UI
      if (e.clientX <= 0 || (e.clientX >= window.innerWidth || e.clientY >= window.innerHeight)) {
        handleLeave(true);
      }
    };

    document.addEventListener("visibilitychange", handleLeave);
    window.addEventListener("blur", handleLeave);
    document.addEventListener("mouseleave", handleMouseLeave);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' || e.key === 'F11' || e.keyCode === 27 || e.keyCode === 122) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', handleKeyDown);

    // Block right-click context menu to prevent accidental blur/focus loss
    const handleContextMenu = (e) => e.preventDefault();
    document.addEventListener("contextmenu", handleContextMenu);

    const handleBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = ''; // Trigger native browser popup
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleLeave);
      window.removeEventListener("blur", handleLeave);
      document.removeEventListener("mouseleave", handleMouseLeave);
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      window.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener("contextmenu", handleContextMenu);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      const overlay = document.getElementById('fullscreen-enforcer');
      if (overlay) overlay.remove();
    };
  }, []);

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(currentQuestionIndex - 1);
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(currentQuestionIndex + 1);
    }
  };

  // Handle navigation to specific question from navigator
  const handleQuestionNavigation = (index) => {
    setCurrentQuestionIndex(index);
  };

  const [isOpen, setIsOpen] = useState(false);

  const handleReviewAndSubmit = () => {
    setIsOpen(true);
  };

  useEffect(() => {
    getQuestions();
  }, []);

  useEffect(() => {
    const handleBackButtonEvent = (e) => {
      e.preventDefault();
      window.history.pushState(null, null, window.location.pathname);

      if (!window.confirm("Are you sure you want to exit?")) {
        window.history.pushState(null, null, window.location.pathname);
      } else {
        navigate("/");
      }
    };

    window.history.pushState(null, null, window.location.pathname);
    window.addEventListener("popstate", handleBackButtonEvent);

    return () => {
      window.removeEventListener("popstate", handleBackButtonEvent);
    };
  }, [navigate]);

  const [selectedAnswers, setSelectedAnswers] = useState([]);

  let question;
  const handleOptionChange = (selectedOption) => {
    const newSelectedAnswers = [...selectedAnswers];
    newSelectedAnswers[currentQuestionIndex] = selectedOption;
    setSelectedAnswers(newSelectedAnswers);
    sessionStorage.setItem(
      "SelectedAnswers",
      JSON.stringify(newSelectedAnswers)
    );
  };

  // Recalculate progress whenever answers change.
  // Formula: answered / total — so 25/50 = exactly 50%.
  // Navigation no longer affects the bar; only saving an answer moves it.
  useEffect(() => {
    if (questions.length === 0) return;
    const answered = selectedAnswers.filter((a) => a && a.OptionId).length;
    setProgress((answered / questions.length) * 100);
  }, [selectedAnswers, questions.length]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds < 10 ? "0" : ""}${remainingSeconds}`;
  };

  const modalOverlayStyle = {
    position: "fixed",
    top: "0",
    left: "0",
    width: "100%",
    height: "100%",
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: "999",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "5px",
    padding: "20px",
    boxShadow: "0px 0px 10px rgba(0, 0, 0, 0.3)",
    maxWidth: "100%",
    overflow: "auto",
  };

  const [progress, setProgress] = useState(0);
  const shuffleArray = (array) => {
    if (!Array.isArray(array)) return array;
    let shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const normalizeOptions = (q) => {
    if (Array.isArray(q.Options)) return q.Options;
    if (typeof q.Options === "string" && q.Options.trim() !== "") {
      return q.Options.split(",").map((optStr, idx) => ({
        OptionId: idx + 1,
        Options: optStr.trim(),
      }));
    }
    return [];
  };

  const getQuestions = async () => {
    setIsLoading(true);

    // Check if questions are already saved in sessionStorage (reconnect / refresh resilience)
    const cachedQuestions = sessionStorage.getItem("ExamQuestions");
    if (cachedQuestions) {
      try {
        const parsed = JSON.parse(cachedQuestions);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setQuestions(parsed);
          question = parsed;
          const savedAnswers = sessionStorage.getItem("SelectedAnswers");
          if (savedAnswers) {
            try {
              setSelectedAnswers(JSON.parse(savedAnswers));
            } catch (e) {
              setSelectedAnswers(Array(parsed.length).fill(0));
            }
          } else {
            setSelectedAnswers(Array(parsed.length).fill(0));
          }
          setIsLoading(false);
          return;
        }
      } catch (e) {
        sessionStorage.removeItem("ExamQuestions");
      }
    }

    try {
      const preloadImage = new Promise((resolve) => {
        const img = new Image();
        img.src = require("../../assets/images/Question-New.webp");
        img.onload = resolve;
        img.onerror = resolve; // Fallback so we don't freeze if image fails
      });

      const [resBasicApti, resSqlBasics, resHardOops, resHardApti] = await Promise.all([
        GetExamQuestion({ CategoryId: 1, LimitCount: 50 }),
        GetExamQuestion({ CategoryId: 2, LimitCount: 50 }),
        GetExamQuestion({ CategoryId: 5, LimitCount: 50 }), // Hard OOPS
        GetExamQuestion({ CategoryId: 6, LimitCount: 50 }), // Hard Apti
        preloadImage // Wait for image to load alongside the APIs!
      ]);

      const basicAptiQuestions = shuffleArray(Array.isArray(resBasicApti?.data) ? resBasicApti.data : []).slice(0, 15);
      const sqlBasicsQuestions = shuffleArray(Array.isArray(resSqlBasics?.data) ? resSqlBasics.data : []).slice(0, 13);
      const hardOopsQuestions = shuffleArray(Array.isArray(resHardOops?.data) ? resHardOops.data : []).slice(0, 12);
      const hardAptiQuestions = shuffleArray(Array.isArray(resHardApti?.data) ? resHardApti.data : []).slice(0, 10);

      const final40Questions = [...basicAptiQuestions, ...hardAptiQuestions, ...sqlBasicsQuestions, ...hardOopsQuestions]
        .map(q => ({ ...q, Options: normalizeOptions(q) }));

      if (final40Questions.length === 0) {
        setIsLoading(false);
        Swal.fire({
          icon: "error",
          title: "Unable to Load Questions",
          text: "Could not retrieve questions from the server. Please check your network connection and retry.",
          confirmButtonText: "Retry Loading",
          confirmButtonColor: "#4F46E5",
          allowOutsideClick: false,
        }).then(() => {
          getQuestions();
        });
        return;
      }

      setQuestions(final40Questions);
      question = final40Questions;
      sessionStorage.setItem("ExamQuestions", JSON.stringify(final40Questions));
      setSelectedAnswers(Array(final40Questions.length).fill(0));
    } catch (error) {
      console.error("Error fetching exam questions:", error);
      setIsLoading(false);
      Swal.fire({
        icon: "error",
        title: "Network Error",
        text: "Failed to connect to the server. Please check your connection and retry.",
        confirmButtonText: "Retry Loading",
        confirmButtonColor: "#4F46E5",
        allowOutsideClick: false,
      }).then(() => {
        getQuestions();
      });
      return;
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {isLoading && <LoadingPopup message="Loading Exam..." />}
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          fontFamily: "Arial, sans-serif",
          height: "100vh",
          backgroundColor: "white",
        }}
      >
        <div
          style={{
            flex: 1,
            position: "relative",
            height: "100%",
            backgroundColor: "#ffffff",
          }}
        >
          <div style={{ position: "absolute", top: "5px", left: "10px", zIndex: 10 }}>
            <img
              src={require("../../assets/images/Evalutech.png")}
              alt="Evalutech Logo"
              style={{ height: "4.5rem" }}
            />
          </div>
          <img
            src={require("../../assets/images/Question-New.webp")}
            alt="Time GIF"
            style={{ width: "100%", height: "100%", objectFit: "contain", objectPosition: "left center" }}
          />
        </div>
        <div
          style={{
            flex: 0.7,
            paddingLeft: "clamp(20px, 4vw, 60px)",
            paddingRight: "clamp(15px, 3vw, 40px)",
            maxWidth: "clamp(450px, 45vw, 900px)",
            height: "100vh",
            overflowY: "auto",
            boxSizing: "border-box",
            position: "relative",
            paddingBottom: "50px",
          }}
        >
          <div
            style={{
              padding: "clamp(10px, 1.5vh, 30px) 0",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
            }}
          >
            <img
              src={require("../../assets/images/swt-logo.png")}
              alt="Sunware Logo"
              style={{ width: "clamp(60px, 8vw, 120px)", marginBottom: "8px" }}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <img
                src={require("../../assets/images/Animation - 1699939128035.gif")}
                alt="Sample GIF"
                style={{ width: "clamp(25px, 3.5vh, 60px)", height: "clamp(25px, 3.5vh, 60px)" }}
              />
              <h2 style={{ margin: 0, fontSize: "clamp(16px, min(2vw, 3vh), 28px)" }}>{formatTime(secondsLeft)}</h2>
            </div>
            <div
              style={{
                width: "100%",
                height: "10px",
                backgroundColor: "gray",
                borderRadius: "5px",
                marginTop: "10px",
                position: "relative",
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: "100%",
                  backgroundColor: "#007BFF",
                  borderRadius: "5px",
                }}
              ></div>
              {progress >= 0 && (
                <div
                  style={{
                    position: "absolute",
                    left: `${Math.min(progress, 94)}%`,
                    top: "15px",
                    textAlign: "center",
                    fontSize: "clamp(10px, 1vw, 16px)",
                  }}
                >
                  ({progress.toFixed(0)}%)
                </div>
              )}
            </div>
          </div>
          <div style={{ padding: "clamp(5px, 1vh, 20px) 0" }}>
            {questions?.length > 0 && (
              <QuestionNavigator
                questions={questions}
                selectedAnswers={selectedAnswers}
                currentQuestionIndex={currentQuestionIndex}
                onQuestionClick={handleQuestionNavigation}
              />
            )}
            {questions?.length > 0 ? (
              <>
                <p style={{ marginBottom: "clamp(10px, 1.5vh, 25px)", fontSize: "clamp(13px, min(1.5vw, 2.5vh), 22px)", lineHeight: "1.5" }}>
                  {currentQuestionIndex + 1}.{" "}
                  {questions[currentQuestionIndex]?.Questions}
                </p>
                <div>
                  {Array.isArray(questions[currentQuestionIndex]?.Options) &&
                    questions[currentQuestionIndex].Options.map((option, index) => (
                      <div key={index} style={{ marginBottom: "clamp(6px, 1vh, 15px)" }}>
                        <label
                          style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "clamp(6px, 1vh, 16px) clamp(8px, 1.5vw, 16px)",
                            border: "1px solid #007BFF",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "clamp(12px, min(1.2vw, 2.2vh), 20px)",
                            backgroundColor:
                              selectedAnswers[currentQuestionIndex]?.Options ===
                                option.Options
                                ? "#C4D6EE"
                                : "white",
                          }}
                        >
                          <input
                            type="radio"
                            name={`answer-${questions[currentQuestionIndex]?.id || currentQuestionIndex}`}
                            value={option.OptionId}
                            style={{ marginRight: "10px", transform: "scale(1.2)" }}
                            checked={
                              selectedAnswers[currentQuestionIndex]?.Options ===
                              option.Options
                            }
                            onChange={() => handleOptionChange(option)}
                          />
                          {option.Options}
                        </label>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              !isLoading && (
                <div style={{ textAlign: "center", padding: "40px 10px" }}>
                  <p style={{ color: "#EF4444", fontWeight: "600", fontSize: "16px", marginBottom: "16px" }}>
                    Questions could not be loaded from server.
                  </p>
                  <CustomButton text="Retry Loading" onClick={getQuestions} />
                </div>
              )
            )}
          </div>
          {questions?.length > 0 && (
            <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px", paddingBottom: "20px" }}>
              {currentQuestionIndex > 0 && (
                <CustomButton text="Previous" onClick={handlePreviousQuestion} />
              )}
              {currentQuestionIndex < questions.length - 1 ? (
                <CustomButton text="Next" onClick={handleNextQuestion} />
              ) : (
                <CustomButton text="Review" onClick={handleReviewAndSubmit} />
              )}
            </div>
          )}
          <div style={{ position: "absolute", bottom: "10px", right: "20px", fontSize: "12px", color: "#999" }}>
            © 2026 Sunware Technologies. All rights reserved.
          </div>
          {isOpen && (
            <div style={modalOverlayStyle}>
              <div style={modalStyle}>
                <ReviewPage
                  isOpen={isOpen}
                  onClose={() => setIsOpen(false)}
                  questions={questions}
                  selectedAnswers={selectedAnswers}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Question;
